// Phase 1.ζ-2 — LockedShell preview harness. Renders every §10.4 cell
// on a single page so the operator (and future iterators) can compare
// states side-by-side without needing real pools in each state.
//
// Mounted in App.jsx via the URL param ?preview=lockedshell. Bypasses
// auth so screenshots are deterministic. Pure render: no DB, no Stripe,
// no Supabase — onUnlock and unlocking flow are wired through local
// useState so the click states are previewable too.
//
// Each cell is rendered inside an isolated container styled like the
// real pool view (matches the layout that LockedShell appears within
// in production — the centered card column).

import { useState } from 'react'
import LockedShell from '../components/LockedShell'

const containerStyle = {
  background: 'var(--bg)',
  color: 'var(--text)',
  minHeight: '100vh',
  padding: '24px 0 80px',
  fontFamily: 'inherit',
}

const cellHeading = {
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: 1.2,
  color: 'var(--text-dim)',
  textTransform: 'uppercase',
  textAlign: 'center',
  margin: '40px auto 4px',
  fontFamily: 'Arial, sans-serif',
}

const cellSubheading = {
  fontSize: 11,
  color: 'var(--text-muted)',
  textAlign: 'center',
  marginBottom: 18,
  fontFamily: 'Arial, sans-serif',
}

const cellWrapper = {
  maxWidth: 520,
  margin: '0 auto',
  padding: '0 16px',
  borderTop: '1px solid var(--border)',
  borderBottom: '1px solid var(--border)',
  background: 'var(--surface)',
}

// Matches the contest_class → price map exposed by src/lib/stripe.js so
// the previews show the same numbers production uses.
const PRICES = {
  ufc_event:        499,
  golf_pickem:      799,
  weekly_pickem:    799,
  daily_pickem:     299,
  bracket_playoffs: 999,
  survivor_season: 1499,
}

const SAMPLE_TIER_PREVIEW = [
  'T1 · ranks 1-10',
  'T2 · ranks 11-20',
  'T3 · ranks 21-30',
  'T4 · ranks 31-40',
  'T5 · ranks 41-50',
  'T6 · rank 51 → end of field',
]

function Cell({ heading, subheading, children }) {
  return (
    <>
      <div style={cellHeading}>{heading}</div>
      <div style={cellSubheading}>{subheading}</div>
      <div style={cellWrapper}>{children}</div>
    </>
  )
}

export default function LockedShellPreview() {
  const [unlocking, setUnlocking] = useState(false)
  const fakeUnlock = () => {
    setUnlocking(true)
    setTimeout(() => setUnlocking(false), 1500)
  }

  return (
    <div style={containerStyle}>
      <div style={{
        maxWidth: 520, margin: '0 auto', padding: '0 16px 12px',
        textAlign: 'center', fontFamily: 'inherit',
      }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>
          LockedShell preview — §10.4 cells
        </div>
        <div style={{
          fontSize: 12, color: 'var(--text-dim)', marginTop: 6,
          fontFamily: 'Arial, sans-serif',
        }}>
          Phase 1.ζ-2 dev harness. Bypasses auth, renders LockedShell
          in every state. Tap any CTA to preview the pending state.
        </div>
      </div>

      <Cell
        heading="Cell 4 · Non-divisions plain CTA"
        subheading="UFC pool (contest_class=ufc_event, $4.99). Used also for pick'em + bracket."
      >
        <LockedShell
          group={{ id: 'preview-ufc', sport: 'UFC', contest_type: 'pick_em' }}
          unlockState={{ unlocked: false, divisionsStatus: null, lockoutPassed: false }}
          contestClass="ufc_event"
          priceCents={PRICES.ufc_event}
          onUnlock={fakeUnlock}
          unlocking={unlocking}
        />
      </Cell>

      <Cell
        heading="Cell 2 · Divisions awaiting odds"
        subheading="Golf:divisions pool, golf_config.snapshotStatus='awaiting_odds' ($7.99)."
      >
        <LockedShell
          group={{ id: 'preview-divisions-aw', sport: 'Golf', contest_type: 'divisions' }}
          unlockState={{ unlocked: false, divisionsStatus: 'awaiting_odds', lockoutPassed: false }}
          contestClass="golf_pickem"
          priceCents={PRICES.golf_pickem}
          onUnlock={fakeUnlock}
          unlocking={unlocking}
        />
      </Cell>

      <Cell
        heading="Cell 3 · Divisions frozen — tier preview + glass card"
        subheading="Golf:divisions pool, snapshotStatus='frozen', tierPreview from tierSnapshot keys."
      >
        <LockedShell
          group={{ id: 'preview-divisions-fr', sport: 'Golf', contest_type: 'divisions' }}
          unlockState={{ unlocked: false, divisionsStatus: 'frozen', lockoutPassed: false }}
          contestClass="golf_pickem"
          priceCents={PRICES.golf_pickem}
          onUnlock={fakeUnlock}
          unlocking={unlocking}
          tierPreview={SAMPLE_TIER_PREVIEW}
        />
      </Cell>

      <Cell
        heading="Cell 1 · Dead pool (lockoutPassed, terminal §10.6)"
        subheading="Any contest_type. lockout_at passed (or start_date passed w/o snapshot). No payment affordance."
      >
        <LockedShell
          group={{ id: 'preview-dead', sport: 'NFL', contest_type: 'pick_em' }}
          unlockState={{ unlocked: false, divisionsStatus: null, lockoutPassed: true }}
          contestClass="daily_pickem"
          priceCents={PRICES.daily_pickem}
          onUnlock={fakeUnlock}
          unlocking={unlocking}
        />
      </Cell>

      <div style={cellHeading}>Transparency check · grandfathered (unlocked)</div>
      <div style={cellSubheading}>
        LockedShell is not rendered at all when unlockState.unlocked is true.
        The mode-component gate's `if (!us.unlocked) return &lt;LockedShell .../&gt;`
        falls through, and today's UI renders unchanged.
      </div>
      <div style={cellWrapper}>
        <div style={{
          padding: '40px 20px', textAlign: 'center',
          fontFamily: 'inherit', color: 'var(--text-dim)',
        }}>
          <div style={{ fontSize: 32, marginBottom: 10 }}>✓</div>
          <div style={{ fontSize: 14, color: 'var(--text)', fontWeight: 600, marginBottom: 6 }}>
            Gate transparent — normal mode renders here.
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'Arial, sans-serif', lineHeight: 1.6, maxWidth: 320, margin: '0 auto' }}>
            Existing pools have unlocked_at backfilled by migration 040.
            deriveUnlockState returns {'{ unlocked: true }'}, the gate
            falls through, and PickemMode / GolfPicksMode / GolfDivisionsMode
            / BracketMode renders its existing flow byte-identical.
          </div>
        </div>
      </div>
    </div>
  )
}
